import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { leaveAdminAPI, guidedPresenceAPI } from '../services/api';
import { useLocation, useNavigate } from 'react-router-dom';
// @ts-ignore
import * as XLSXStyle from 'xlsx-js-style';

interface LeaveRequest {
  id: number;
  employee_id: number;
  request_type: string;
  start_date: string;
  end_date?: string;
  start_time?: string;
  end_time?: string;
  duration_hours?: number;
  duration_days?: number;
  status: string;
  approver_id?: number;
  approver_notes?: string;
  request_date: string;
  approval_date?: string;
  notes?: string;
  leave_reason?: string;
  employee_name: string;
  employee_surname: string;
  matricola?: string;
  dipartimento?: string;
  approver_username?: string;
}

interface ManagedGroup {
  id: number;
  name: string;
}

const TYPE_LABELS: Record<string, string> = {
  ferie: 'Ferie',
  permesso: 'Permesso',
  malattia: 'Malattia',
  altri: 'Altri',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'In attesa',
  approved: 'Approvata',
  rejected: 'Rifiutata',
  cancelled: 'Annullata',
};

const STATUS_COLORS: Record<string, 'warning' | 'success' | 'error' | 'default'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
  cancelled: 'default',
};

const TYPE_COLORS: Record<string, 'info' | 'secondary' | 'warning' | 'default'> = {
  ferie: 'info',
  permesso: 'secondary',
  malattia: 'warning',
  altri: 'default',
};

const formatDate = (dateStr: string) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const LeaveHistory: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filtri
  const now = new Date();
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(now.getFullYear(), 0, 1);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => now.toISOString().split('T')[0]);
  const [requestType, setRequestType] = useState('tutti');
  const [status, setStatus] = useState('tutti');
  const [searchText, setSearchText] = useState('');

  // Gruppi
  const [groups, setGroups] = useState<ManagedGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<number>(0);

  useEffect(() => {
    const loadGroups = async () => {
      try {
        const data = await guidedPresenceAPI.getManagedGroups();
        setGroups(data || []);
      } catch {
        // Ignore - non tutti gli admin hanno gruppi
      }
    };
    loadGroups();
  }, []);

  useEffect(() => {
    const state = location.state as
      | {
          focusStatus?: string;
          focusRequestType?: string;
          focusEmployeeName?: string;
          focusGroupId?: number;
        }
      | null;

    if (!state) {
      return;
    }

    if (state.focusStatus) {
      setStatus(state.focusStatus);
    }
    if (state.focusRequestType) {
      setRequestType(state.focusRequestType);
    }
    if (state.focusEmployeeName) {
      setSearchText(state.focusEmployeeName);
    }
    if (typeof state.focusGroupId === 'number') {
      setSelectedGroupId(state.focusGroupId);
    }

    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const queryStatus = params.get('status');
    const queryType = params.get('request_type');
    const queryEmployee = params.get('employee');
    const queryGroupId = Number(params.get('group_id'));

    if (queryStatus && status !== queryStatus) {
      setStatus(queryStatus);
    }

    if (queryType && requestType !== queryType) {
      setRequestType(queryType);
    }

    if (queryEmployee !== null && searchText !== queryEmployee) {
      setSearchText(queryEmployee);
    }

    if (params.has('group_id') && Number.isFinite(queryGroupId) && selectedGroupId !== queryGroupId) {
      setSelectedGroupId(queryGroupId);
    }
  }, [location.search, requestType, searchText, selectedGroupId, status]);

  const contextParams = new URLSearchParams(location.search);
  const hasLeaveContext =
    contextParams.has('status') ||
    contextParams.has('request_type') ||
    contextParams.has('employee') ||
    contextParams.has('group_id');

  const activeContextLabels = [
    status !== 'tutti' ? `Stato: ${STATUS_LABELS[status] || status}` : null,
    requestType !== 'tutti' ? `Tipo: ${TYPE_LABELS[requestType] || requestType}` : null,
    searchText ? `Dipendente: ${searchText}` : null,
    selectedGroupId > 0 ? `Gruppo: ${groups.find((group) => group.id === selectedGroupId)?.name || selectedGroupId}` : null,
  ].filter(Boolean) as string[];

  const clearLeaveContext = () => {
    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0];
    const today = now.toISOString().split('T')[0];

    setDateFrom(startOfYear);
    setDateTo(today);
    setRequestType('tutti');
    setStatus('tutti');
    setSearchText('');
    setSelectedGroupId(0);

    navigate(location.pathname, { replace: true });
  };

  const loadHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await leaveAdminAPI.getLeaveHistory({
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        request_type: requestType !== 'tutti' ? requestType : undefined,
        status: status !== 'tutti' ? status : undefined,
        group_id: selectedGroupId > 0 ? selectedGroupId : undefined,
      });
      setRequests(data.requests || []);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Errore caricamento storico');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, requestType, status, selectedGroupId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const filtered = requests.filter((r) => {
    if (!searchText) return true;
    const text = searchText.toLowerCase();
    return (
      r.employee_name?.toLowerCase().includes(text) ||
      r.employee_surname?.toLowerCase().includes(text) ||
      r.matricola?.toLowerCase().includes(text) ||
      r.leave_reason?.toLowerCase().includes(text)
    );
  });

  // Statistiche
  const stats = {
    totale: filtered.length,
    ferie: filtered.filter((r) => r.request_type === 'ferie').length,
    permessi: filtered.filter((r) => r.request_type === 'permesso').length,
    malattia: filtered.filter((r) => r.request_type === 'malattia').length,
    approvate: filtered.filter((r) => r.status === 'approved').length,
    inAttesa: filtered.filter((r) => r.status === 'pending').length,
    rifiutate: filtered.filter((r) => r.status === 'rejected').length,
    totGiorniFerie: filtered.filter((r) => r.request_type === 'ferie' && r.status === 'approved')
      .reduce((sum, r) => sum + (r.duration_days || 0), 0),
    totOrePermessi: filtered.filter((r) => r.request_type === 'permesso' && r.status === 'approved')
      .reduce((sum, r) => sum + (r.duration_hours || 0), 0),
    totGiorniMalattia: filtered.filter((r) => r.request_type === 'malattia' && r.status === 'approved')
      .reduce((sum, r) => sum + (r.duration_days || 0), 0),
  };

  const handlePrint = () => window.print();

  const handleExport = () => {
    const wb = XLSXStyle.utils.book_new();
    const rows: any[][] = [];

    rows.push(['Storico Ferie e Permessi']);
    rows.push([`Periodo: ${formatDate(dateFrom)} - ${formatDate(dateTo)}`]);
    rows.push([]);

    rows.push([
      'Dipendente', 'Matricola', 'Tipo', 'Causale', 'Data Inizio', 'Data Fine',
      'Giorni', 'Ore', 'Stato', 'Data Richiesta', 'Approvato da', 'Note',
    ]);

    filtered.forEach((r) => {
      rows.push([
        `${r.employee_surname || ''} ${r.employee_name || ''}`.trim(),
        r.matricola || '-',
        TYPE_LABELS[r.request_type] || r.request_type,
        r.leave_reason || '-',
        formatDate(r.start_date),
        r.end_date ? formatDate(r.end_date) : '-',
        r.duration_days != null ? r.duration_days.toFixed(1) : '-',
        r.duration_hours != null ? r.duration_hours.toFixed(1) : '-',
        STATUS_LABELS[r.status] || r.status,
        formatDate(r.request_date),
        r.approver_username || '-',
        r.notes || '-',
      ]);
    });

    rows.push([]);
    rows.push(['Riepilogo']);
    rows.push(['Totale richieste', stats.totale]);
    rows.push(['Ferie approvate (giorni)', stats.totGiorniFerie.toFixed(1)]);
    rows.push(['Permessi approvati (ore)', stats.totOrePermessi.toFixed(1)]);
    rows.push(['Malattia approvata (giorni)', stats.totGiorniMalattia.toFixed(1)]);

    const ws = XLSXStyle.utils.aoa_to_sheet(rows);

    const headerStyle = {
      fill: { fgColor: { rgb: '1565C0' } },
      font: { bold: true, color: { rgb: 'FFFFFF' } },
      alignment: { horizontal: 'center', vertical: 'center' },
      border: { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } },
    };
    for (let c = 0; c < 12; c++) {
      const addr = XLSXStyle.utils.encode_cell({ r: 3, c });
      if (ws[addr]) ws[addr].s = headerStyle;
    }

    ws['!cols'] = [
      { wch: 25 }, { wch: 12 }, { wch: 12 }, { wch: 25 }, { wch: 12 }, { wch: 12 },
      { wch: 8 }, { wch: 8 }, { wch: 12 }, { wch: 12 }, { wch: 15 }, { wch: 30 },
    ];

    XLSXStyle.utils.book_append_sheet(wb, ws, 'Storico');
    const wbout = XLSXStyle.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Storico_Ferie_Permessi_${dateFrom}_${dateTo}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Titolo - visibile anche in stampa */}
      <Typography variant="h4" fontWeight={700} gutterBottom className="print-visible">
        Storico Ferie e Permessi
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom className="print-visible">
        Periodo: {formatDate(dateFrom)} - {formatDate(dateTo)}
        {requestType !== 'tutti' && ` | Tipo: ${TYPE_LABELS[requestType]}`}
        {status !== 'tutti' && ` | Stato: ${STATUS_LABELS[status]}`}
      </Typography>

      {hasLeaveContext && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={clearLeaveContext}>
              Azzera filtri
            </Button>
          }
        >
          <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
            <Typography variant="body2">
              Storico aperto con contesto applicato dalla dashboard.
            </Typography>
            {activeContextLabels.map((label) => (
              <Chip key={label} label={label} size="small" variant="outlined" />
            ))}
          </Box>
        </Alert>
      )}

      {/* Filtri - nascosti in stampa */}
      <Paper sx={{ p: 2, mb: 2 }} className="no-print">
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <FilterIcon color="action" />
          <Typography variant="subtitle2" color="text.secondary">Filtri</Typography>
        </Box>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <TextField
            type="date"
            label="Da"
            size="small"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160 }}
          />
          <TextField
            type="date"
            label="A"
            size="small"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160 }}
          />
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Tipo</InputLabel>
            <Select value={requestType} label="Tipo" onChange={(e) => setRequestType(e.target.value)}>
              <MenuItem value="tutti">Tutti</MenuItem>
              <MenuItem value="ferie">Ferie</MenuItem>
              <MenuItem value="permesso">Permesso</MenuItem>
              <MenuItem value="malattia">Malattia</MenuItem>
              <MenuItem value="altri">Altri</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Stato</InputLabel>
            <Select value={status} label="Stato" onChange={(e) => setStatus(e.target.value)}>
              <MenuItem value="tutti">Tutti</MenuItem>
              <MenuItem value="approved">Approvate</MenuItem>
              <MenuItem value="pending">In attesa</MenuItem>
              <MenuItem value="rejected">Rifiutate</MenuItem>
              <MenuItem value="cancelled">Annullate</MenuItem>
            </Select>
          </FormControl>
          {groups.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Gruppo</InputLabel>
              <Select value={selectedGroupId} label="Gruppo" onChange={(e) => setSelectedGroupId(Number(e.target.value))}>
                <MenuItem value={0}>Tutti i dipendenti</MenuItem>
                {groups.map((g) => (
                  <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            size="small"
            placeholder="Cerca dipendente..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ minWidth: 200 }}
          />
          <IconButton onClick={loadHistory}><RefreshIcon /></IconButton>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Statistiche */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
          <Chip label={`Totale: ${stats.totale}`} variant="outlined" />
          <Chip label={`Ferie: ${stats.ferie} (${stats.totGiorniFerie.toFixed(1)} gg)`} color="info" size="small" />
          <Chip label={`Permessi: ${stats.permessi} (${stats.totOrePermessi.toFixed(1)} h)`} color="secondary" size="small" />
          <Chip label={`Malattia: ${stats.malattia} (${stats.totGiorniMalattia.toFixed(1)} gg)`} color="warning" size="small" />
          <Box sx={{ mx: 1, borderLeft: '1px solid', borderColor: 'divider', height: 24 }} />
          <Chip label={`Approvate: ${stats.approvate}`} color="success" size="small" variant="outlined" />
          <Chip label={`In attesa: ${stats.inAttesa}`} color="warning" size="small" variant="outlined" />
          {stats.rifiutate > 0 && <Chip label={`Rifiutate: ${stats.rifiutate}`} color="error" size="small" variant="outlined" />}

          {/* Azioni - nascoste in stampa */}
          <Box sx={{ ml: 'auto' }} className="no-print">
            <Tooltip title="Stampa report">
              <Button size="small" variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint} sx={{ mr: 1 }}>
                Stampa
              </Button>
            </Tooltip>
            <Button
              size="small"
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={filtered.length === 0}
            >
              Esporta Excel
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Tabella */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      ) : filtered.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">Nessuna richiesta trovata per i filtri selezionati.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 700 }}>Dipendente</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }} align="center">Tipo</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }}>Causale</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }} align="center">Periodo</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }} align="right">Durata</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }} align="center">Stato</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }} align="center">Data Rich.</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }}>Approvato da</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }}>Note</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {r.employee_surname} {r.employee_name}
                    </Typography>
                    {r.matricola && (
                      <Typography variant="caption" color="text.secondary">{r.matricola}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={TYPE_LABELS[r.request_type] || r.request_type}
                      color={TYPE_COLORS[r.request_type] || 'default'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                      {r.leave_reason || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                      {formatDate(r.start_date)}
                      {r.end_date && r.end_date !== r.start_date && (
                        <> - {formatDate(r.end_date)}</>
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600}>
                      {r.duration_days != null && r.duration_days > 0
                        ? `${r.duration_days.toFixed(1)} gg`
                        : r.duration_hours != null && r.duration_hours > 0
                        ? `${r.duration_hours.toFixed(1)} h`
                        : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={STATUS_LABELS[r.status] || r.status}
                      color={STATUS_COLORS[r.status] || 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                      {formatDate(r.request_date)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                      {r.approver_username || '-'}
                    </Typography>
                    {r.approver_notes && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {r.approver_notes}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.notes || '-'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* CSS per la stampa */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-visible { display: block !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          @page { size: landscape; margin: 1cm; }
        }
      `}</style>
    </Container>
  );
};

export default LeaveHistory;
